import {useEffect, useMemo, useState} from "react";
import {useForm} from "antd/es/form/Form";
import {ScheduleItem} from "../types/schedule";
import {scheduleService} from "../services/scheduleService";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {StaffItem} from "../types/staff";

export const useScheduleController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [scheduleList, setScheduleList] = useState<ScheduleItem[]>([])
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleItem | undefined>(undefined)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [scheduleForm] = useForm()
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)


  const tableData = useMemo(() => {
    if (searchValue && searchValue.trim().length) {
      const filteredData = scheduleList.filter(item => {
          const staffData = item.staff as StaffItem
          const {fullName} = staffData
          return fullName.toLowerCase().includes(searchValue.trim().toLowerCase())
        }
      );

      return filteredData
    }
    return scheduleList
  }, [scheduleList, searchValue])

  const sortedTableData = useMemo(() => {
    if (tableData.length) {
      return [...tableData].sort((a, b) => {
        const aName = typeof a.staff === 'object' && a.staff?.fullName ? a.staff.fullName : '';
        const bName = typeof b.staff === 'object' && b.staff?.fullName ? b.staff.fullName : '';
        return aName.localeCompare(bName);
      });
    }
    return [];
  }, [tableData]);


  useEffect(() => {
    if (selectedSchedule) {
      const formData = {
        staff: typeof selectedSchedule?.staff === "object" ? selectedSchedule.staff.$id : '',
        availableDays: selectedSchedule.availableDays,
        availableTime: selectedSchedule.availableTime
      }
      scheduleForm.setFieldsValue(formData)
    } else {
      scheduleForm.resetFields()
    }
  }, [selectedSchedule])

  useEffect(() => {
    fetchScheduleList([])
  }, [lastUpdate])


  const toggleCreateModalVisible = () => {
    const newValue = !isCreateModalOpen
    setIsCreateModalOpen(newValue)
    if (newValue === false) {
      setSelectedSchedule(undefined)
    }
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const createScheduleRecord = async (data: ScheduleItem) => {
    setIsLoading(true)
    try {
      const createdItem = await scheduleService.create(data)
      openSuccessNotification("Запис успішно створено!")
      toggleCreateModalVisible()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка створення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const updateScheduleRecord = async (data: ScheduleItem) => {
    setIsLoading(true)
    try {
      const updatedRecord = await scheduleService.update(data)
      toggleCreateModalVisible()
      toggleLastUpdate()
      openSuccessNotification("Запис успішно оновлено!")
    } catch (error) {
      showErrorNotification({message: "Помилка оновлення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const deleteScheduleRecordHandler = async (id: string) => {
    setIsLoading(true)
    try {
      await scheduleService.delete(id)
      openSuccessNotification("Запис успішно видалено!")
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка видалення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const fetchScheduleList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await scheduleService.list(queries)
      setScheduleList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження графіку прийому!"});
    } finally {
      setIsLoading(false)
    }
  }

  const submitFormHandler = (data: ScheduleItem) => {
    if (selectedSchedule) {
      updateScheduleRecord({...data, $id: selectedSchedule.$id})
    } else {
      createScheduleRecord(data)
    }
  }

  const confirmDeleteRecord = (id: string) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити запис?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteScheduleRecordHandler(id)
        },
      });
  }

  const editButtonHandler = (record: ScheduleItem) => {
    setSelectedSchedule(record)
    toggleCreateModalVisible()
    // scheduleForm.setFields(record)
  }


  return {
    isLoading,
    scheduleForm,
    scheduleList,
    selectedSchedule,
    isCreateModalOpen,
    toggleCreateModalVisible,
    submitFormHandler,
    confirmDeleteRecord,
    editButtonHandler,
    setSearchValue,
    tableData,
    sortedTableData
  }
}

export default useScheduleController